import React from "react";
import { Grid, InputAdornment, TextField } from "@mui/material";
import "./FormTextField.css";
import RenderSvg from "../renderSvg/RenderSvg";

const customMuiInputStyle = {
  "& .MuiOutlinedInput-root": {
    "& fieldset": {
      borderColor: "#EAEAEA",
    },
    "&:hover fieldset": {
      borderColor: "#B9B9B9",
    },
    "&.Mui-focused fieldset": {
      borderColor: "#1C7EA0",
    },
  },
  "& .MuiInputLabel-root": {
    color: "#B5B5B5",
    fontSize: "13px",
  },
  "& .MuiInputLabel-root.Mui-focused": {
    color: "#1C7EA0",
  },
};

const customMuiDisabledInputStyle = {
  "& .MuiOutlinedInput-root.Mui-disabled": {
    "& fieldset": {
      borderColor: "#EAEAEA",
    },
  },
};

const infoBorderStyle = {
  "& .MuiOutlinedInput-root": {
    "&.Mui-error": {
      "& fieldset": {
        borderColor: "#cd8e15",
      },
    },
  },
};

const nonEditableGreyTheme = {
  "& .MuiOutlinedInput-root": {
    backgroundColor: "#FAFAFA",
    "& fieldset": {
      border: "none",
    },
  },
  "& input[type=number]::-webkit-outer-spin-button, & input[type=number]::-webkit-inner-spin-button":
    {
      "-webkit-appearance": "none",
      margin: 0,
    },
};

interface FormTextFieldProps {
  name: string;
  namePc?: string;
  nameCs?: string;
  nameOther?: string;
  value: string;
  label: string;
  handleChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  isMandatory?: boolean;
  isDisabled?: boolean;
  customTheme?: any;
  isMultiQtyType?: boolean;
  csQty?: string;
  pcQty?: string;
  otherQty?: string;
  error?: string;
  disclaimer?: boolean;
  onFocusOut?: () => void;
  uom?: string;
  otherUnitName?: string;
}

const FormTextField: React.FC<FormTextFieldProps> = ({
  name,
  namePc = "quantityPc",
  nameCs = "quantityCs",
  nameOther = "quantityOther",
  value,
  label,
  handleChange,
  isMandatory = false,
  isDisabled = false,
  customTheme = customMuiInputStyle,
  isMultiQtyType = false,
  csQty = 0,
  pcQty = 0,
  otherQty = 0,
  error,
  disclaimer,
  onFocusOut,
  uom = "",
  otherUnitName
}) => {
  const ConfigDictionary = JSON.parse(localStorage.getItem("ConfigDictionary") || "{}");
  const { caseUnitLabel, otherUnitLabel, packUnitLabel } = ConfigDictionary;

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (["e", "E", "+", "-", "."].includes(event.key)) {
      event.preventDefault(); // Disallow "e", "E", "+", and "-" keys
    }
  };

  // Prevent scroll incrementing/decrementing the number
  const handleWheel = (event: React.WheelEvent<HTMLInputElement>) => {
    event.preventDefault(); // Prevent the default scroll behavior
  };

  const handlePaste = (event: React.ClipboardEvent<HTMLInputElement>) => {
    const pasteData = event.clipboardData.getData("text");
    // Allow only digits in the pasted content
    if (!/^\d+$/.test(pasteData)) {
      event.preventDefault(); // Block the paste if invalid data is found
    }
  };

  return (
    <div className="FormTextField">
      <div className="FormTextField_label">
        {label}
        {isMandatory && <span style={{ color: "red" }}>*</span>}
      </div>
      {!isMultiQtyType ? (
        <TextField
          name={name}
          value={value}
          onChange={handleChange}
          variant="outlined"
          size="small"
          disabled={isDisabled}
          sx={
            isDisabled
              ? customMuiDisabledInputStyle
              : {
                  ...customMuiInputStyle,
                  "& .MuiOutlinedInput-root": {
                    ...customMuiInputStyle["& .MuiOutlinedInput-root"],
                    ...infoBorderStyle["& .MuiOutlinedInput-root"],
                  },
                }
          }
          error={Boolean(error)}
          helperText={
            error && (
              <div style={{ color: "#cd8e15", display: "flex" }}>
                <div style={{ width: "20px", paddingRight: "6px" }}>
                  <RenderSvg name="warningIcon" />
                </div>
                {error}
              </div>
            )
          }
          onBlur={onFocusOut}
          className="error-message-left"
        />
      ) : (
        <Grid container spacing={2}>
          <Grid item xs={otherUnitLabel ? 4 : 6}>
            <TextField
              fullWidth
              name={nameCs}
              type="number"
              value={csQty}
              onChange={handleChange}
              variant="outlined"
              size="small"
              disabled={
                isDisabled ||
                (!uom.toLowerCase().includes("cs") && !uom.toLowerCase().includes("all"))
              }
              onPaste={handlePaste}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="start">
                    <span style={{ color: "#909090" }}>{caseUnitLabel}</span>
                  </InputAdornment>
                ),
                inputProps: {
                  min: 0,
                },
              }}
              sx={
                isDisabled ||
                (!uom.toLowerCase().includes("cs") && !uom.toLowerCase().includes("all"))
                  ? nonEditableGreyTheme
                  : {
                      ...customTheme,
                      "& input[type=number]::-webkit-outer-spin-button, & input[type=number]::-webkit-inner-spin-button":
                        {
                          "-webkit-appearance": "none",
                          margin: 0,
                        },
                    }
              }
              error={!disclaimer && Boolean(error)}
              helperText={
                error && (
                  <div
                    style={{
                      color: disclaimer ? "#cd8e15" : "#f44336",
                      display: "flex",
                      alignItems: "flex-start",
                      lineHeight: "15px",
                    }}
                  >
                    {disclaimer && (
                      <div style={{ width: "20px", paddingRight: "6px", paddingTop: "2px" }}>
                        <RenderSvg name="warningIcon" />
                      </div>
                    )}
                    {error}
                  </div>
                )
              }
              onBlur={onFocusOut}
              onKeyDown={handleKeyDown}
              className="error-message-left"
            />
          </Grid>
          {otherUnitLabel && (
            <Grid item xs={4}>
              <TextField
                fullWidth
                name={nameOther}
                type="number"
                value={otherQty}
                onChange={handleChange}
                variant="outlined"
                size="small"
                disabled={
                  isDisabled ||
                  (!uom.toLowerCase().includes("other") && !uom.toLowerCase().includes("all"))
                }
                onPaste={handlePaste}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="start">
                      <span style={{ color: "#909090" }}>{otherUnitName ? otherUnitName:otherUnitLabel}</span>
                    </InputAdornment>
                  ),
                  inputProps: {
                    min: 0,
                  },
                }}
                sx={
                  isDisabled ||
                  (!uom.toLowerCase().includes("other") && !uom.toLowerCase().includes("all"))
                    ? nonEditableGreyTheme
                    : {
                        ...customTheme,
                        "& input[type=number]::-webkit-outer-spin-button, & input[type=number]::-webkit-inner-spin-button":
                          {
                            "-webkit-appearance": "none",
                            margin: 0,
                          },
                      }
                }
                error={!disclaimer && Boolean(error)}
                onBlur={onFocusOut}
                onKeyDown={handleKeyDown}
                className="error-message-left"
              />
            </Grid>
          )}
          <Grid item xs={otherUnitLabel ? 4 : 6}>
            <TextField
              fullWidth
              name={namePc}
              type="number"
              value={pcQty}
              onChange={handleChange}
              variant="outlined"
              size="small"
              disabled={
                isDisabled ||
                (!uom.toLowerCase().includes("ea") && !uom.toLowerCase().includes("all"))
              }
              onPaste={handlePaste}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="start">
                    <span style={{ color: "#909090" }}>{packUnitLabel}</span>
                  </InputAdornment>
                ),
                inputProps: {
                  min: 0,
                },
              }}
              sx={
                isDisabled ||
                (!uom.toLowerCase().includes("ea") && !uom.toLowerCase().includes("all"))
                  ? nonEditableGreyTheme
                  : {
                      ...customTheme,
                      "& input[type=number]::-webkit-outer-spin-button, & input[type=number]::-webkit-inner-spin-button":
                        {
                          "-webkit-appearance": "none",
                          margin: 0,
                        },
                    }
              }
              error={!disclaimer && Boolean(error)}
              onBlur={onFocusOut}
              onKeyDown={handleKeyDown}
              onWheel={handleWheel}
            />
          </Grid>
        </Grid>
      )}
    </div>
  );
};

export default FormTextField;
